class CvGameHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-cv-hero-slide]'));
    if (this.slides.length < 2 || this.dataset.cvHeroSliderReady === 'true') return;

    this.dataset.cvHeroSliderReady = 'true';
    this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.dragging = false;
    this.suppressClick = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.show(this.activeIndex);
    this.bindEvents();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  bindEvents() {
    this.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.addEventListener('pointercancel', this.onPointerCancel.bind(this));
    this.addEventListener('click', this.onClick.bind(this), true);
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
  }

  onPointerDown(event) {
    if (event.target.closest('.cv-game-storefront__search')) return;

    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.dragging = true;
    this.suppressClick = false;
    this.classList.add('cv-game-storefront__hero--dragging');
    this.stopAutoplay();
  }

  onPointerMove(event) {
    if (!this.dragging) return;

    const deltaX = event.clientX - this.pointerStartX;
    const deltaY = event.clientY - this.pointerStartY;

    if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.suppressClick = true;
    }
  }

  onPointerUp(event) {
    if (!this.dragging) return;

    const deltaX = event.clientX - this.pointerStartX;
    const deltaY = event.clientY - this.pointerStartY;
    this.resetDragState();

    if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      deltaX < 0 ? this.next() : this.previous();
    }

    this.startAutoplay();
  }

  onPointerCancel() {
    this.resetDragState();
    this.startAutoplay();
  }

  onClick(event) {
    if (!this.suppressClick) return;

    event.preventDefault();
    event.stopPropagation();
    this.suppressClick = false;
  }

  resetDragState() {
    this.dragging = false;
    this.classList.remove('cv-game-storefront__hero--dragging');
  }

  startAutoplay() {
    if (this.reducedMotion || this.slides.length < 2 || this.autoplayTimer) return;

    this.autoplayTimer = window.setInterval(() => this.next(), 5000);
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;

    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  next() {
    this.show(this.activeIndex + 1);
  }

  previous() {
    this.show(this.activeIndex - 1);
  }

  show(index) {
    this.activeIndex = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.tabIndex = isActive && slide.hasAttribute('href') ? 0 : -1;
    });
  }
}

if (!customElements.get('cv-game-hero-slider')) {
  customElements.define('cv-game-hero-slider', CvGameHeroSlider);
}

(() => {
  const rarityOrder = ['RRR', 'RR', 'R', 'C', 'T', 'TD', 'TDR', 'PR', 'ORR', 'R+', 'SR', 'FR', 'FFR', 'SEC', 'DSR', 'SIR', 'SIR+', 'EX', 'EXC', 'EXRRR', 'EXS', 'SP', 'SSR', 'LSR', 'H', 'Re', 'RGGR', 'IZR', 'PGS', '15thSP'];
  const nationOrder = ['ドラゴンエンパイア', 'ダークステイツ', 'ブラントゲート', 'ケテルサンクチュアリ', 'ストイケイア', 'リリカルモナステリオ'];
  const requests = new Map();

  function matches(tags, rarities, nations) {
    return (!rarities.length || rarities.some((value) => tags.includes(value))) &&
      (!nations.length || nations.some((value) => tags.includes(value)));
  }

  async function loadProducts(collectionUrl, sortBy, resultsSection = 'cv-vg-filter-results') {
    const firstUrl = new URL(collectionUrl, window.location.origin);
    if (sortBy) firstUrl.searchParams.set('sort_by', sortBy);
    firstUrl.searchParams.set('section_id', resultsSection);
    if (resultsSection === 'cv-vg-collection-results') {
      new URL(window.location.href).searchParams.forEach((value, name) => {
        if (name.startsWith('filter.')) firstUrl.searchParams.append(name, value);
      });
    }
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview) firstUrl.searchParams.set('preview_theme_id', preview);
    const key = firstUrl.href;
    if (requests.has(key)) return requests.get(key);

    const request = (async () => {
      const products = new Map();
      const visited = new Set();
      let url = firstUrl;
      while (url) {
        if (url.origin !== window.location.origin || visited.has(url.href)) throw new Error('Invalid pagination');
        visited.add(url.href);
        const response = await fetch(url.href, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = document.querySelector('[data-cv-vg-results]');
        if (!results) throw new Error('Missing collection results');
        results.querySelectorAll('[data-cv-product]').forEach((card) => {
          products.set(card.dataset.productId, {
            card,
            tags: JSON.parse(card.dataset.productTags),
          });
        });
        const next = results.dataset.nextUrl;
        url = next ? new URL(next, firstUrl) : null;
        if (url) {
          url.searchParams.set('section_id', resultsSection);
          if (sortBy) url.searchParams.set('sort_by', sortBy);
          if (preview) url.searchParams.set('preview_theme_id', preview);
        }
      }
      return Array.from(products.values());
    })();
    requests.set(key, request);
    try {
      return await request;
    } catch (error) {
      requests.delete(key);
      throw error;
    }
  }

  function initialize(scope = document) {
    scope.querySelectorAll("[data-cv-game='vg'] [data-cv-vg-collection]").forEach((section) => {
      if (section.dataset.cvFiltersReady) return;
      section.dataset.cvFiltersReady = 'true';
      const details = section.querySelector('[data-cv-filters]');
      const options = section.querySelector('[data-cv-filter-options]');
      const status = section.querySelector('[data-cv-filter-status]');
      const reset = section.querySelector('[data-cv-filter-reset]');
      const retry = section.querySelector('[data-cv-filter-retry]');
      const selection = section.querySelector('[data-cv-filter-selection]');
      const grid = section.querySelector('.cv-game-storefront__product-grid');
      const moreWrap = section.querySelector('[data-cv-filter-more-wrap]');
      const more = section.querySelector('[data-cv-filter-more]');
      const isCollectionPage = section.hasAttribute('data-cv-collection-page');
      const keyword = section.querySelector('[data-cv-collection-keyword]');
      const sort = section.querySelector('[data-cv-collection-sort]');
      const nativePagination = section.querySelector('[data-cv-native-pagination]');
      const nativeFilters = section.querySelector('[data-cv-native-filters]');
      const initialParams = new URL(window.location.href).searchParams;
      if (keyword) keyword.value = initialParams.get('cv_q') || '';
      const limit = Number(section.dataset.productsLimit) || 12;
      let products = null;
      let loading = false;
      let visibleLimit = limit;
      details.hidden = false;
      details.open = window.matchMedia('(min-width: 990px)').matches;

      function selected(group) {
        return Array.from(options.querySelectorAll(`button[data-filter-group="${group}"][aria-pressed="true"]`), (button) => button.value);
      }

      function render() {
        const rarities = selected('rarity');
        const nations = selected('nation');
        const query = keyword ? keyword.value.trim().toLowerCase() : '';
        const filtered = products.filter((product) => matches(product.tags, rarities, nations) &&
          (!query || (product.card.dataset.searchText || '').includes(query)));
        const count = rarities.length + nations.length;
        selection.textContent = count ? `（${count}件選択）` : '';
        reset.hidden = !count;
        grid.replaceChildren(...filtered.slice(0, visibleLimit).map((product) => product.card.cloneNode(true)));
        if (!filtered.length) {
          const empty = document.createElement('li');
          empty.className = 'cv-game-storefront__empty';
          empty.textContent = '条件に合う商品がありません。選択を変更してください。';
          grid.append(empty);
        }
        status.hidden = false;
        status.textContent = `${filtered.length}件中 ${Math.min(filtered.length, visibleLimit)}件を表示`;
        moreWrap.hidden = filtered.length <= visibleLimit;
        if (nativePagination) nativePagination.hidden = true;
        if (isCollectionPage) {
          const url = new URL(window.location.href);
          [['cv_rarity', rarities.join(',')], ['cv_nation', nations.join(',')], ['cv_q', query]].forEach(([name, value]) => {
            if (value) url.searchParams.set(name, value);
            else url.searchParams.delete(name);
          });
          url.searchParams.delete('page');
          window.history.replaceState(window.history.state, '', url.href);
        }
      }

      function buildGroup(label, group, values) {
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'cv-game-storefront__filter-group';
        const legend = document.createElement('legend');
        legend.textContent = label;
        fieldset.append(legend);
        const chips = document.createElement('div');
        chips.className = 'cv-game-storefront__filter-chips';
        values.forEach((value) => {
          const chip = document.createElement('button');
          chip.className = 'cv-game-storefront__filter-chip';
          chip.type = 'button';
          chip.value = value;
          chip.dataset.filterGroup = group;
          const initialValues = isCollectionPage ? (initialParams.get(group === 'rarity' ? 'cv_rarity' : 'cv_nation') || '').split(',') : [];
          chip.setAttribute('aria-pressed', initialValues.includes(value) ? 'true' : 'false');
          chip.textContent = value;
          chips.append(chip);
        });
        fieldset.append(chips);
        if (!values.length) {
          const empty = document.createElement('p');
          empty.textContent = 'この商品一覧には対象タグがありません。';
          fieldset.append(empty);
        }
        options.append(fieldset);
      }

      async function load() {
        if (products || loading) return;
        loading = true;
        retry.hidden = true;
        status.hidden = false;
        status.textContent = '絞り込み用の商品情報を読み込んでいます…';
        details.setAttribute('aria-busy', 'true');
        try {
          products = await loadProducts(section.dataset.collectionUrl, section.dataset.sortBy, section.dataset.resultsSection);
          const tags = new Set(products.flatMap((product) => product.tags));
          buildGroup('レアリティ', 'rarity', rarityOrder.filter((value) => tags.has(value)));
          buildGroup('国家', 'nation', nationOrder.filter((value) => tags.has(value)));
          render();
        } catch (error) {
          status.textContent = '商品情報を読み込めませんでした。もう一度お試しください。';
          retry.hidden = false;
        } finally {
          loading = false;
          details.removeAttribute('aria-busy');
        }
      }

      details.addEventListener('toggle', () => { if (details.open) load(); });
      if (details.open || isCollectionPage) load();
      if (keyword) keyword.addEventListener('input', () => { if (products) { visibleLimit = limit; render(); } });
      if (sort) sort.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        window.location.href = url.href;
      });
      if (nativeFilters) nativeFilters.addEventListener('submit', (event) => {
        event.preventDefault();
        const url = new URL(window.location.href);
        Array.from(url.searchParams.keys()).forEach((name) => {
          if (name.startsWith('filter.')) url.searchParams.delete(name);
        });
        new FormData(nativeFilters).forEach((value, name) => {
          if (value !== '') url.searchParams.append(name, value);
        });
        url.searchParams.delete('page');
        window.location.href = url.href;
      });
      retry.addEventListener('click', load);
      options.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-filter-group]');
        if (!button) return;
        button.setAttribute('aria-pressed', button.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
        visibleLimit = limit;
        render();
      });
      reset.addEventListener('click', () => {
        options.querySelectorAll('[aria-pressed="true"]').forEach((button) => { button.setAttribute('aria-pressed', 'false'); });
        visibleLimit = limit;
        render();
      });
      more.addEventListener('click', () => { visibleLimit += 24; render(); });
    });
  }

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
